import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'

import {
  applyForRound,
  approveRound,
  cancelRound,
  createClassificationScheme,
  createLoanProduct,
  disburseRound,
  executeGuaranteeOffset,
  loanKeys,
  openLoanAccount,
  overrideClassification,
  postRepayment,
  recordAppraisal,
  recordRecovery,
  rejectRound,
  restructureRound,
  runAgeing,
  runClassification,
  setDisclosureConfig,
  settleRound,
  updateLoanProduct,
  waivePenalty,
  writeOffRound,
} from '@/api/loans-api'
import type {
  ApplyForRoundRequest,
  ApproveRoundRequest,
  CancelRoundRequest,
  ClassificationOverrideRequest,
  ClassificationSchemeRequest,
  DisburseRoundRequest,
  DisclosureConfigRequest,
  GuaranteeOffsetRequest,
  LoanCycleDetail,
  LoanProductRequest,
  OpenLoanAccountRequest,
  PortfolioRunRequest,
  PostRepaymentRequest,
  RecordAppraisalRequest,
  RecordRecoveryRequest,
  RejectRoundRequest,
  RestructureRoundRequest,
  SettleRoundRequest,
  WaivePenaltyRequest,
  WriteOffRoundRequest,
} from '@/api/loans-types'

/**
 * Commands over the loans module.
 *
 * Two invalidation rules run through all of them.
 *
 * A command acts on a *round*, and almost every one of them changes a figure the account list
 * shows — the current round's balance, its arrears, its status. So the round, its schedule, its
 * settlement quote, the account that carries it and the account lists are all invalidated
 * together. Anything narrower leaves an arrears figure on screen that the collection just paid.
 *
 * Nothing is written into the cache from a response, even where the response carries the whole
 * round. Five of these are countersigned and answer with the round *unchanged* when the branch's
 * policy defers them — writing that into the cache would be indistinguishable from writing a
 * genuine result, and re-reading is what tells the two apart.
 */
function invalidateCycle(queryClient: QueryClient, cycleId: string, accountId?: string) {
  void queryClient.invalidateQueries({ queryKey: loanKeys.cycle(cycleId) })
  void queryClient.invalidateQueries({ queryKey: loanKeys.schedule(cycleId) })
  void queryClient.invalidateQueries({ queryKey: loanKeys.cycles() })
  if (accountId) {
    void queryClient.invalidateQueries({ queryKey: loanKeys.account(accountId) })
  }
  void queryClient.invalidateQueries({ queryKey: loanKeys.accountLists() })
}

export function useOpenLoanAccount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: OpenLoanAccountRequest) => openLoanAccount(request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: loanKeys.accountLists() })
    },
  })
}

export function useApplyForRound(accountId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: ApplyForRoundRequest) => applyForRound(accountId, request),
    onSuccess: (cycle: LoanCycleDetail) => {
      invalidateCycle(queryClient, cycle.summary.id, accountId)
    },
  })
}

export function useRecordAppraisal(cycleId: string, accountId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: RecordAppraisalRequest) => recordAppraisal(cycleId, request),
    onSuccess: () => {
      invalidateCycle(queryClient, cycleId, accountId)
    },
  })
}

/** Dual control — answers a DualControlResult, so the screen reads that and not the round. */
export function useApproveRound(cycleId: string, accountId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: ApproveRoundRequest) => approveRound(cycleId, request),
    onSuccess: () => {
      invalidateCycle(queryClient, cycleId, accountId)
    },
  })
}

export function useRejectRound(cycleId: string, accountId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: RejectRoundRequest) => rejectRound(cycleId, request),
    onSuccess: () => {
      invalidateCycle(queryClient, cycleId, accountId)
    },
  })
}

export function useCancelRound(cycleId: string, accountId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: CancelRoundRequest) => cancelRound(cycleId, request),
    onSuccess: () => {
      invalidateCycle(queryClient, cycleId, accountId)
    },
  })
}

/** Dual control, and refused to whoever approved the round. */
export function useDisburseRound(cycleId: string, accountId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: DisburseRoundRequest) => disburseRound(cycleId, request),
    onSuccess: () => {
      invalidateCycle(queryClient, cycleId, accountId)
    },
  })
}

export function usePostRepayment(cycleId: string, accountId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: PostRepaymentRequest) => postRepayment(cycleId, request),
    onSuccess: () => {
      invalidateCycle(queryClient, cycleId, accountId)
    },
  })
}

export function useSettleRound(cycleId: string, accountId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: SettleRoundRequest) => settleRound(cycleId, request),
    onSuccess: () => {
      invalidateCycle(queryClient, cycleId, accountId)
    },
  })
}

/** Countersigned; answers with the round, unchanged when it is queued. */
export function useWaivePenalty(cycleId: string, accountId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: WaivePenaltyRequest) => waivePenalty(cycleId, request),
    onSuccess: () => {
      invalidateCycle(queryClient, cycleId, accountId)
    },
  })
}

/** Countersigned; answers with the round, unchanged when it is queued. Posts nothing. */
export function useRestructureRound(cycleId: string, accountId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: RestructureRoundRequest) => restructureRound(cycleId, request),
    onSuccess: () => {
      invalidateCycle(queryClient, cycleId, accountId)
    },
  })
}

/** Countersigned; answers with the round, unchanged when it is queued. */
export function useWriteOffRound(cycleId: string, accountId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: WriteOffRoundRequest) => writeOffRound(cycleId, request),
    onSuccess: () => {
      invalidateCycle(queryClient, cycleId, accountId)
    },
  })
}

export function useRecordRecovery(cycleId: string, accountId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: RecordRecoveryRequest) => recordRecovery(cycleId, request),
    onSuccess: () => {
      invalidateCycle(queryClient, cycleId, accountId)
    },
  })
}

/** Countersigned; answers with the round, unchanged when it is queued. */
export function useExecuteGuaranteeOffset(cycleId: string, accountId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: GuaranteeOffsetRequest) => executeGuaranteeOffset(cycleId, request),
    onSuccess: () => {
      invalidateCycle(queryClient, cycleId, accountId)
    },
  })
}

/** Countersigned; answers with the round, unchanged when it is queued. */
export function useOverrideClassification(cycleId: string, accountId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: ClassificationOverrideRequest) =>
      overrideClassification(cycleId, request),
    onSuccess: () => {
      invalidateCycle(queryClient, cycleId, accountId)
    },
  })
}

/** Dual control. A new product is invisible until somebody countersigns it. */
export function useCreateLoanProduct() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: LoanProductRequest) => createLoanProduct(request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: loanKeys.products() })
    },
  })
}

/** Dual control. Never reaches a round whose terms are already frozen. */
export function useUpdateLoanProduct(productId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: LoanProductRequest) => updateLoanProduct(productId, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: loanKeys.products() })
    },
  })
}

/** Dual control. */
export function useCreateClassificationScheme() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: ClassificationSchemeRequest) => createClassificationScheme(request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: loanKeys.classificationSchemes() })
    },
  })
}

/**
 * Dual control. The invalidation matters even on a deferred outcome: the row is unchanged, but
 * refetching is what proves that to the screen instead of the screen assuming it.
 */
export function useSetDisclosureConfig() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: DisclosureConfigRequest) => setDisclosureConfig(request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: loanKeys.disclosureConfig() })
    },
  })
}

/**
 * The two portfolio runs.
 *
 * Both change every live round at the branch — ageing accrues penalty, classification moves the
 * provision — so both invalidate the whole module rather than any one round. The result itself is
 * kept by the screen: it is a report of what the run did, not a cached view of anything.
 */
export function useRunAgeing() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: PortfolioRunRequest) => runAgeing(request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: loanKeys.all })
    },
  })
}

export function useRunClassification() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: PortfolioRunRequest) => runClassification(request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: loanKeys.all })
    },
  })
}
